import type {
  PdfJsPageDetails,
  Placeholder,
  PlaceholderResize,
  PlaceholderTemplateRef,
  WebPageDetails
} from '@/composables/placement/placementTypes.ts';
import { constants } from '@/constants/constants.ts';
import type { DragResizeContext } from '@deepcloud/draggable';
import { useGlobalStore } from '@/stores/global.ts';
import type { AutographPosition } from '@/types/AutographPosition.ts';
import { cloneDeep } from '@/composables/useUtils.ts';
import type { PDFPageProxy } from '@/services/signatureService.ts';
import { SignatureType } from '@/types/enums/SignatureType.ts';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import type { SigneePolicy } from '@/types/SigneePolicy.ts';
import type { Document } from '@/types/Document.ts';
import type { SignDocument } from '@/types/SignDocument.ts';
import type { Signee } from '@/types/Signee.ts';
import type { UpdateSignee } from '@/types/deepsign/UpdateSignee.ts';
import type {
  RouteLocationNormalizedLoaded,
  RouteLocationNormalizedLoadedGeneric
} from 'vue-router';
import { waitFor } from '@abacus/wait-for';
import { isPlaceholderForSignee } from '@/utils/signeeSignings.ts';
import { getPdfPageBoxes } from '@/utils/pdfPageBoxes.ts';
import { nextTick } from 'vue';

function enforceDefaultDimensionsIfOversized(
  placeholder: Placeholder,
  scale: number
): void {
  if (!placeholder?.position) return;

  const isSignatureOrSeal =
    placeholder.placeHolderType === PlaceholderType.SEAL ||
    placeholder.placeHolderType === PlaceholderType.SIGNATURE;
  const isAnnotation =
    placeholder.placeHolderType === PlaceholderType.Annotation;

  if (!isSignatureOrSeal && !isAnnotation) return;

  const maxWidth = isAnnotation
    ? pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.L.WIDTH) * scale
    : pixelsToPoints(constants.SIGNATURE_SIZE.L.WIDTH) * scale;
  const maxHeight = isAnnotation
    ? pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.L.HEIGHT) * scale
    : pixelsToPoints(constants.SIGNATURE_SIZE.L.HEIGHT) * scale;

  // If a placeholder exceeds allowed max size, reset it back to defaults (M)
  if (
    placeholder.position.width > maxWidth ||
    placeholder.position.height > maxHeight
  ) {
    placeholder.position.width = isAnnotation
      ? pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.M.WIDTH) * scale
      : pixelsToPoints(constants.SIGNATURE_SIZE.M.WIDTH) * scale;
    placeholder.position.height = isAnnotation
      ? pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.M.HEIGHT) * scale
      : pixelsToPoints(constants.SIGNATURE_SIZE.M.HEIGHT) * scale;
  }
}

// On the sign step, once a signature placeholder is authorized
// (`canModifyAutographPosition`), the current user's OWN placeholder in an
// envelope (multi-file document) must stay draggable/resizable/removable
// even after it's already been placed (`hasAutographPosition`) — unlike an
// ordinary placeholder, which locks once placed (see the final return in
// `getPlaceholderDraggableInitialState`) — since every change here is
// persisted ahead of time via `signStore.updateSignee` (the final sign
// request can only ever carry a single file's position). Scoped to the
// signee's own placeholder only — every other signee's placeholder shown on
// this page must stay read-only. Quorum slots are excluded (their shared
// slot is never self-placeable) and keep the regular, draft-only policy
// checks in `getPlaceholderDraggableInitialState`. Mirrors
// `shouldPersistSignPlacement` in `useDocumentContext.ts`.
function isOwnEnvelopeSignStepPlaceholder(
  placeholder: Placeholder | Partial<Placeholder>,
  routeName: RouteLocationNormalizedLoaded['name'],
  document: Document | SignDocument | undefined | null,
  isQuorumGroupMember: boolean,
  isSignature: boolean
): boolean {
  if (!isSignature || routeName !== 'document-sign' || isQuorumGroupMember) {
    return false;
  }

  const signContext = (document as SignDocument | null | undefined)
    ?.signContext;
  const isEnvelope = (document?.files?.length ?? 0) > 1;

  return (
    isEnvelope &&
    Boolean(signContext?.email) &&
    placeholder?.id === signContext?.signeeId
  );
}

export function getPlaceholderDraggableInitialState(
  placeholder: Placeholder | Partial<Placeholder>,
  routeReference:
    RouteLocationNormalizedLoaded | RouteLocationNormalizedLoadedGeneric,
  document: Document | SignDocument | undefined | null
) {
  const routeName = routeReference.name;
  const isDraft =
    routeName === 'document-create' || routeName === 'p-document-create';
  const canModifyAutographByDocument = Boolean(
    document?.documentPolicy?.canModifyAutographPosition
  );
  const canModifyAutographBySignee = Boolean(
    (placeholder?.policy as SigneePolicy)?.canModifyAutographPosition
  );
  const canModifyAutographBySigningPolicy = Boolean(
    placeholder?.signingPolicy?.canModifyAutographPosition
  );
  const isAnnotation =
    placeholder?.placeHolderType === PlaceholderType.Annotation;
  const isSignature = placeholder?.signatureType === SignatureType.SIGNATURE;
  const isSeal = placeholder?.signatureType === SignatureType.SEAL;
  const hasAutographPosition = Boolean(placeholder?.autographPosition);
  // A quorum group's shared signature slot (`signeeGroup.requiredSignatures >
  // 0`) has no single owning signee, so the per-signee/per-signing policies
  // above may still read false for it. In the draft/create flow the creator
  // must still be able to move/resize/remove it like any other placeholder
  // it just created.
  const isQuorumGroupMember = Boolean(placeholder?.isQuorumSlot);

  if (isAnnotation) return true;

  const canModifyAutographPosition =
    canModifyAutographBySignee ||
    canModifyAutographByDocument ||
    canModifyAutographBySigningPolicy ||
    (isDraft && isQuorumGroupMember);

  const isInviteCase =
    isSignature &&
    routeName === 'sign-id' &&
    !hasAutographPosition &&
    Boolean(document?.documentPolicy?.canModifyAutographPosition);

  if (isInviteCase) return true;

  // In the draft/create flow the creator owns every placeholder and must always
  // be able to move, resize and remove it — regardless of the (possibly still
  // false) per-signee autograph policy that a freshly added signee starts with.
  // This must be evaluated before the policy gate below.
  if (isDraft && isSignature && canModifyAutographPosition) return true;

  if (!canModifyAutographPosition) return false;

  if (isSeal && !hasAutographPosition && canModifyAutographByDocument)
    return true;

  if (
    isOwnEnvelopeSignStepPlaceholder(
      placeholder,
      routeName,
      document,
      isQuorumGroupMember,
      isSignature
    )
  ) {
    return true;
  }

  return routeName === 'document-sign' && isSignature && !hasAutographPosition;
}

export function setValues(
  placeholder: Placeholder,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updates: { [key: string]: any }[]
) {
  updates.forEach((update) => {
    Object.keys(update).forEach((key) => {
      if (key in placeholder) {
        placeholder[key] = cloneDeep(update[key]);
      }
    });
  });
}

export async function transformPDFCoordinatesToWebCoordinates(
  autographPosition: AutographPosition
) {
  const { x, y, width, height, pageNumber } = autographPosition;
  const { pdfJsPageDetails, currentPageNode } = await getPageInfo(pageNumber);

  const { viewport, pageXRotated, pageYRotated, pageHeightRotated } =
    pdfJsPageDetails;
  const { scale } = viewport;

  const transFormedX = Math.abs(
    getLeftOffset(currentPageNode) + (x - pageXRotated) * scale
  );

  // `x`/`y` are MediaBox-relative; the rendered page starts at the view box,
  // so the view box's offset within the MediaBox has to be taken back out.
  const pdfDistanceFromTop = pageHeightRotated - (y - pageYRotated) - height;
  const transFormedY = Math.abs(
    getTopOffset(currentPageNode) + pdfDistanceFromTop * scale
  );

  return {
    pageNumber,
    x: transFormedX,
    y: transFormedY,
    width: width * scale,
    height: height * scale
  };
}

// Inverse of `transformPDFCoordinatesToWebCoordinates`: converts a placeholder's
// current on-screen pixel position (relative to `.page-content`) into
// scale-independent PDF-space coordinates. Used to cache a placeholder's
// position right after a manual drag/resize so it reflects the most recent
// user interaction, independently of whether it has been persisted to the
// backend (`autographPosition`) yet.
export async function transformWebCoordinatesToPDFCoordinates(
  position: AutographPosition
): Promise<AutographPosition> {
  const { pageNumber, x, y, width, height } = position;
  const { pdfJsPageDetails, currentPageNode } = await getPageInfo(pageNumber);
  const { viewport, pageXRotated, pageYRotated, pageHeightRotated } =
    pdfJsPageDetails;
  const { scale } = viewport;

  const calculatedX =
    (x - getLeftOffset(currentPageNode)) / scale + pageXRotated;
  const relativeY = y - getTopOffset(currentPageNode);
  const calculatedY =
    pageHeightRotated - relativeY / scale - height / scale + pageYRotated;

  return {
    pageNumber,
    x: calculatedX,
    y: calculatedY,
    width: width / scale,
    height: height / scale
  };
}

export async function calculatePlaceholderCoordinates(
  placeholder: Placeholder,
  placeholderTemplateRef: DragResizeContext | null
) {
  if (!placeholderTemplateRef) {
    throw new Error('Placeholder template ref is not defined!');
  }
  const { pdfJsPageDetails, currentPageNode } = await getPageInfo(
    placeholder.position!.pageNumber || 1
  );
  const { viewport, pageXRotated, pageYRotated, pageHeightRotated } =
    pdfJsPageDetails;
  const { scale } = viewport;

  const { width, height, draggingElement } =
    placeholderTemplateRef as DragResizeContext;

  const element = draggingElement as HTMLElement | null | undefined;

  // The Draggable's context (width/height) and inline styles (left/top) are the
  // source of truth while an element is interacted with. For a freshly rendered
  // placeholder (e.g. just dropped on a newly selected envelope file) the
  // context may not be measured yet, leaving width/height undefined — which
  // would make y/width/height compute to NaN (serialized as null). Fall back to
  // the placeholder's own position, which holds the same screen-pixel values.
  //
  // `isAutoPlaced` marks a placeholder whose position was computed by our own
  // auto-placement/drop logic and has NOT since been manually
  // dragged/resized via the Draggable component. For such placeholders the
  // underlying `@deepcloud/draggable` library's internal position
  // initialization is asynchronous (it resolves its own `:x`/`:y` props over
  // several internal `nextTick`s after mount), so `element.style.left/top`
  // can still hold the library's transient "0,0" default here even though it
  // is a finite number (not NaN), silently bypassing the fallback below. In
  // that case `placeholder.position` is the only reliable source, so it is
  // preferred over the DOM read entirely. Once the user actually drags/resizes
  // the placeholder, `isAutoPlaced` is flipped to `false` and the DOM
  // read (which reflects the live drag) becomes the source of truth again.
  const trustDomPosition = element && placeholder.isAutoPlaced !== true;
  const styleLeft = trustDomPosition ? parseInt(element.style.left) : NaN;
  const styleTop = trustDomPosition ? parseInt(element.style.top) : NaN;

  const effectiveLeft = Number.isFinite(styleLeft)
    ? styleLeft
    : (placeholder.position?.x ?? 0);
  const effectiveTop = Number.isFinite(styleTop)
    ? styleTop
    : (placeholder.position?.y ?? 0);
  const effectiveWidth =
    typeof width === 'number' && Number.isFinite(width) && width > 0
      ? width
      : (placeholder.position?.width ?? 0);
  const effectiveHeight =
    typeof height === 'number' && Number.isFinite(height) && height > 0
      ? height
      : (placeholder.position?.height ?? 0);

  const relativeXToCurrentPageWithinDocument = Math.abs(
    effectiveLeft - getLeftOffset(currentPageNode)
  );

  const calculatedX =
    relativeXToCurrentPageWithinDocument / scale + pageXRotated;

  const relativeYToCurrentPageWithinDocument = Math.abs(
    effectiveTop - getTopOffset(currentPageNode)
  );

  const calculatedY =
    Math.abs(
      pageHeightRotated -
        relativeYToCurrentPageWithinDocument / scale -
        effectiveHeight / scale
    ) + pageYRotated;

  return {
    x: calculatedX,
    y: calculatedY,
    width: effectiveWidth / scale,
    height: effectiveHeight / scale
  };
}

export function getTopOffset(elem: HTMLElement | Element) {
  const container = document.querySelector('.page-content') as HTMLElement;
  if (!container) {
    return (elem as HTMLElement).offsetTop;
  }

  const elemRect = elem.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return elemRect.top - containerRect.top + container.scrollTop;
}

export function getLeftOffset(elem: HTMLElement | Element) {
  const container = document.querySelector('.page-content') as HTMLElement;
  if (!container) {
    return (elem as HTMLElement).offsetLeft;
  }

  const elemRect = elem.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return elemRect.left - containerRect.left + container.scrollLeft;
}

type PlaceholderIdentityFields = Partial<
  Pick<
    Placeholder,
    'uid' | 'id' | 'fileId' | 'signatureSlot' | 'placeHolderType'
  >
>;

/**
 * Stable identity of a placeholder. Prefers the envelope `uid`
 * (signeeId-signeeGroupId-fileId-signatureSlot-type) and otherwise composes a
 * key from the available metadata so every placeholder of a signee is unique
 * even when the signee owns multiple placeholders across files/slots.
 */
export function getPlaceholderUid(
  placeholder: PlaceholderIdentityFields
): string {
  return (
    placeholder.uid ||
    [
      placeholder.id || 'placeholder',
      placeholder.fileId || 'document',
      placeholder.signatureSlot ?? 0,
      placeholder.placeHolderType || PlaceholderType.SIGNATURE
    ].join('-')
  );
}

export function getPlaceholderDomId(
  placeholder: PlaceholderIdentityFields
): string {
  return `placeholder-${getPlaceholderUid(placeholder)}`;
}

export function getPlaceholderPreviewDomId(
  placeholder: PlaceholderIdentityFields
): string {
  return `signature-preview-${getPlaceholderUid(placeholder)}`;
}

export function getPlaceholderEmailDomId(
  placeholder: PlaceholderIdentityFields
): string {
  return `signature-email-${getPlaceholderUid(placeholder)}`;
}

export async function createPlaceholderPayload(
  placeHolder: Placeholder | undefined | null,
  placeholderRefs: PlaceholderTemplateRef[] | undefined | null
) {
  if (
    !placeHolder ||
    !placeHolder.position?.pageNumber ||
    !placeHolder.isVisible
  ) {
    return null;
  }

  const templateRefs = Array.isArray(placeholderRefs) ? placeholderRefs : [];

  const match = templateRefs.find((placeHolderRef) => {
    const draggingElement = (placeHolderRef.context as DragResizeContext)
      ?.draggingElement as HTMLElement | null | undefined;
    if (!draggingElement) return null;

    if (
      !draggingElement.dataset?.placeholderType ||
      draggingElement.dataset.placeholderType !== placeHolder.placeHolderType
    ) {
      return null;
    }

    return draggingElement.id === getPlaceholderDomId(placeHolder);
  });

  if (!match) return null;

  const placeholderTemplateRef = match.context as DragResizeContext;

  const { x, y, width, height } = await calculatePlaceholderCoordinates(
    placeHolder,
    placeholderTemplateRef
  );

  return {
    pageNumber: placeHolder.position.pageNumber,
    x,
    y,
    width,
    height
  };
}

/**
 * Aggregate all signature placeholders of a single signee into one
 * `UpdateSignee` payload. Produces a `fileAutographPositions` map keyed by
 * fileId plus the legacy `autographPosition` (first position of the primary
 * file) so single-file documents keep working. This is the canonical way to
 * persist a signee that may own multiple placeholders.
 */
export async function buildSigneeUpdatePayload(
  signee: Pick<Signee, 'signeeId' | 'email' | 'signeeComment'>,
  placeholders: Placeholder[],
  placeholderRefs: PlaceholderTemplateRef[] | undefined | null,
  document?: Document | SignDocument | null,
  primaryFileId?: string | null
): Promise<UpdateSignee> {
  const signaturePlaceholders = placeholders
    .filter(
      (placeholder) =>
        isPlaceholderForSignee(
          placeholder,
          signee.signeeId,
          document?.signeeGroups
        ) && placeholder.placeHolderType !== PlaceholderType.Annotation
    )
    .sort(
      (left, right) =>
        (left.signatureSlot ?? left.index) -
        (right.signatureSlot ?? right.index)
    );

  const fileAutographPositions: Record<string, AutographPosition[]> = {};

  for (const placeholder of signaturePlaceholders) {
    const fileId =
      placeholder.fileId || document?.files?.[0]?.fileId || 'document';

    if (!fileAutographPositions[fileId]) {
      fileAutographPositions[fileId] = [];
    }

    const position = await createPlaceholderPayload(
      placeholder,
      placeholderRefs
    );
    if (position) {
      fileAutographPositions[fileId].push(position);
    }
  }

  const fallbackFileId = primaryFileId || document?.files?.[0]?.fileId || null;
  const primaryPositions = fallbackFileId
    ? fileAutographPositions[fallbackFileId]
    : undefined;
  const autographPosition =
    primaryPositions?.[0] ??
    Object.values(fileAutographPositions).find(
      (positions) => positions.length > 0
    )?.[0] ??
    null;

  return {
    email: signee.email ?? null,
    comment: signee.signeeComment ?? null,
    autographPosition,
    fileAutographPositions
  };
}

/**
 * Persist every placeholder for a set of signees. Placeholders are grouped by
 * signee so a signee that owns multiple placeholders is updated once with a
 * complete `fileAutographPositions` payload. This is the canonical persistence
 * path for all ordered/unordered add/update flows.
 *
 * Takes the full candidate `signees` array (rather than a `resolveSignee`
 * lookup) because a quorum group's shared slot placeholder no longer carries
 * a signeeId to resolve in the first place — `isPlaceholderForSignee` is used
 * instead to find which signee(s) in the array a given placeholder belongs to.
 */
export async function persistSigneePlaceholders(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createStore: any,
  placeholders: Placeholder[],
  placeholderRefs: PlaceholderTemplateRef[] | undefined | null,
  signees: Signee[],
  document?: Document | SignDocument | null
): Promise<void> {
  const signeeGroups = document?.signeeGroups;
  const ownedPlaceholders = placeholders.filter(
    (placeholder) => placeholder.placeHolderType !== PlaceholderType.Annotation
  );

  const relevantSignees = signees.filter((signee) =>
    ownedPlaceholders.some((placeholder) =>
      isPlaceholderForSignee(placeholder, signee.signeeId, signeeGroups)
    )
  );

  // Build every signee's payload first (these read live DOM positions), then
  // persist them all in parallel and apply the local-data updates in one batch.
  // Doing the updates one-by-one in a sequential loop made the invitee cards
  // flip to "placed" individually as each request resolved; batching lets them
  // update together. Mirrors the already-parallel remove flow.
  const updates: { signeeId: string; payload: UpdateSignee }[] = [];
  for (const match of relevantSignees) {
    // The autograph-modify permission can be granted per file (signing-level
    // policy) even when the signee-level policy forbids it. Allow the full
    // placement payload when the signee-level policy OR any per-file signing
    // policy grants modification, OR when the signee actually has a placed
    // placeholder in this batch: a freshly added/placed signee has no signings
    // yet and a false signee-level policy, but the creator just placed a
    // position via the UI (mirrors the drag-and-drop flow, which never gates),
    // so the positions must be persisted.
    const hasPlacedPlaceholder = ownedPlaceholders.some(
      (placeholder) =>
        isPlaceholderForSignee(placeholder, match.signeeId, signeeGroups) &&
        (placeholder.isVisible ||
          placeholder.isPlaced ||
          Boolean(placeholder.autographPosition))
    );
    const canModify =
      (match.policy as SigneePolicy)?.canModifyAutographPosition ||
      Boolean(
        match.signings?.some(
          (signing) => signing.policy?.canModifyAutographPosition
        )
      ) ||
      hasPlacedPlaceholder;

    const payload: UpdateSignee = canModify
      ? await buildSigneeUpdatePayload(
          match,
          placeholders,
          placeholderRefs,
          document
        )
      : {
          email: match?.email || null,
          comment: match?.signeeComment || null
        };

    updates.push({ signeeId: match.signeeId, payload });
  }

  const responses = await Promise.all(
    updates.map((update) =>
      createStore.updateInvitee(
        createStore.document!.documentId,
        update.signeeId,
        update.payload
      )
    )
  );

  for (const response of responses) {
    createStore.updateInviteeInLocalData(response.data);
  }
}

/**
 * Persists the current envelope signee's placeholders on the sign step right
 * after an auto-place (the toolbar/sidebar "place signature" button). Only
 * reached when `shouldPersistSignPlacement` holds (envelope + sign route +
 * isSignee — see `useDocumentContext.ts`), so the payload is always built in
 * full, bypassing the draft-only policy gate the same way
 * `PlaceholderWrapper.vue`'s drag/resize persistence does. Waits for the
 * just-placed placeholder(s)' Draggable to settle/remount first so the
 * payload reads their real, final DOM coordinates rather than a stale
 * pre-placement position — mirrors `InviteeCard.vue`'s manual "place" flow —
 * then persists via the sign store instead of the create store.
 */
export async function persistAutoPlacedSignStepPlaceholders(
  signStore: {
    document?: SignDocument | null;
    updateSignee: (
      documentId: string,
      signeeId: string,
      payload: Partial<UpdateSignee>
    ) => Promise<unknown>;
  },
  signee: Pick<Signee, 'signeeId' | 'email' | 'signeeComment'> | undefined,
  placedPlaceholders: Placeholder[],
  allPlaceholders: Placeholder[],
  placeholderRefs: PlaceholderTemplateRef[] | undefined | null,
  requestPlaceholderRemount: (uids?: string[]) => void
): Promise<void> {
  if (!signee || !signStore.document) return;

  await nextTick();
  const uids = placedPlaceholders
    .map((placeholder) => placeholder.uid)
    .filter((uid): uid is string => Boolean(uid));
  if (uids.length > 0) {
    await waitForDraggableToSettle();
    requestPlaceholderRemount(uids);
  }
  await nextTick();

  const payload = await buildSigneeUpdatePayload(
    signee,
    allPlaceholders,
    placeholderRefs,
    signStore.document
  );
  await signStore.updateSignee(
    signStore.document.documentId,
    signee.signeeId,
    payload
  );
}

function getPageLimits(
  pageNumber: number,
  pageNodes: HTMLCollectionOf<HTMLElement>
) {
  const currentPage = pageNodes[pageNumber - 1];

  if (currentPage && currentPage instanceof HTMLElement) {
    return {
      top: currentPage.offsetTop,
      right: currentPage.clientWidth,
      bottom: currentPage.offsetTop + currentPage.clientHeight,
      left: currentPage.offsetLeft
    };
  }
}

export function getPdfDocument() {
  const globalStore = useGlobalStore();
  const pdfPreview =
    globalStore.pdfPreviewRef?.pdfPreview || globalStore.pdfPreviewRef;
  return pdfPreview?.doc;
}

export async function getPage(
  pageNumber: number
): Promise<PDFPageProxy | undefined> {
  return getPdfDocument().getPage(pageNumber || 1);
}

export async function getPageNodeWithRetry(
  pageNumber: number,
  pdfPageIdentifier: string
): Promise<
  | {
      pageNodes: HTMLCollectionOf<Element>;
      currentPageNode: HTMLElement | undefined;
      webPageDetails: WebPageDetails;
    }
  | undefined
> {
  const pageNodes = await waitFor(() => {
    const pageNodes = document.getElementsByClassName(
      pdfPageIdentifier
    ) as HTMLCollectionOf<HTMLElement>;

    return pageNodes?.length ? pageNodes : null;
  });

  const currentPageNode = pageNodes[pageNumber - 1];

  if (pageNodes && currentPageNode?.style) {
    const webPageDetails: WebPageDetails = {
      scale: parseFloat(
        currentPageNode.style.getPropertyValue('--scale-factor')
      ),
      width: parseFloat(currentPageNode.style.getPropertyValue('--width')),
      height: parseFloat(currentPageNode.style.getPropertyValue('--height')),
      clientWidth: currentPageNode.clientWidth,
      clientHeight: currentPageNode.clientHeight,
      offsetTop: currentPageNode.offsetTop,
      offsetLeft: currentPageNode.offsetLeft,
      pageBoundingRect: currentPageNode.getBoundingClientRect()
    };

    // The page node can exist before the PDF renderer has written its
    // `--width`/`--height`/`--scale-factor` CSS vars (notably right after an
    // envelope file switch). Returning here would yield width/height 0 and a
    // scale of 0, collapsing transformed placeholders to 0×0. Keep retrying
    // until the page reports real, finite dimensions.
    const hasValidDimensions =
      Number.isFinite(webPageDetails.width) &&
      Number.isFinite(webPageDetails.height) &&
      webPageDetails.width > 0 &&
      webPageDetails.height > 0;

    if (hasValidDimensions) {
      return {
        pageNodes,
        currentPageNode,
        webPageDetails
      };
    }
  }
}

/**
 * A PDF box (`/MediaBox`, `/CropBox`, …) is an array of two diagonally
 * opposite corners — index 0/1 is NOT guaranteed to be the lower-left one.
 * `[50 100 645 942]` and `[645 942 50 100]` describe the same rectangle.
 */
function normalizeBox(box: number[]): [number, number, number, number] {
  return [
    Math.min(box[0], box[2]),
    Math.min(box[1], box[3]),
    Math.max(box[0], box[2]),
    Math.max(box[1], box[3])
  ];
}

/**
 * `/Rotate` is a clockwise display rotation that may be negative or exceed a
 * full turn (`-90` → `270`, `450` → `90`). Anything that is not a multiple of
 * 90° after normalization is treated as 0°, mirroring the backend.
 */
export function normalizePageRotation(rotate: number | undefined): number {
  const normalized = (((rotate ?? 0) % 360) + 360) % 360;
  return normalized === 90 || normalized === 180 || normalized === 270
    ? normalized
    : 0;
}

/**
 * The backend expects autograph positions relative to the lower-left corner of
 * the **MediaBox in the rotated page view** — the CropBox is explicitly
 * ignored. pdf.js however renders the `view` box (`CropBox ∩ MediaBox`), so
 * screen coordinates are relative to the rendered *view* box instead.
 *
 * Both boxes share the same page rotation, so the difference between the two
 * reference frames is a pure translation in the rotated view. The returned
 * `pageXRotated`/`pageYRotated` are exactly that translation: the position of
 * the rendered page's lower-left corner relative to the MediaBox's lower-left
 * corner, both in the rotated view. They must be added when converting screen
 * → PDF and subtracted when converting PDF → screen.
 *
 * `pageWidthRotated`/`pageHeightRotated` are the real dimensions of the
 * rendered (view) box in the rotated view — not its max coordinates, which is
 * only the same thing for a box whose origin sits at (0,0).
 *
 * Per-rotation origins (see backend spec):
 * - 0°   → mediaBox(Xmin, Ymin)
 * - 90°  → mediaBox(Xmax, Ymin)
 * - 180° → mediaBox(Xmax, Ymax)
 * - 270° → mediaBox(Xmin, Ymax)
 */
export function getRotatedPageGeometry(
  view: number[],
  mediaBox: number[],
  rotation: number
) {
  const [viewXMin, viewYMin, viewXMax, viewYMax] = normalizeBox(view);
  const [mediaXMin, mediaYMin, mediaXMax, mediaYMax] = normalizeBox(mediaBox);

  let pageXRotated: number;
  let pageYRotated: number;

  switch (normalizePageRotation(rotation)) {
    case 90:
      pageXRotated = viewYMin - mediaYMin;
      pageYRotated = mediaXMax - viewXMax;
      break;
    case 180:
      pageXRotated = mediaXMax - viewXMax;
      pageYRotated = mediaYMax - viewYMax;
      break;
    case 270:
      pageXRotated = mediaYMax - viewYMax;
      pageYRotated = viewXMin - mediaXMin;
      break;
    default:
      pageXRotated = viewXMin - mediaXMin;
      pageYRotated = viewYMin - mediaYMin;
  }

  const viewWidth = viewXMax - viewXMin;
  const viewHeight = viewYMax - viewYMin;
  const isPdfPageRotated = normalizePageRotation(rotation) % 180 !== 0;

  return {
    pageXRotated,
    pageYRotated,
    pageWidthRotated: isPdfPageRotated ? viewHeight : viewWidth,
    pageHeightRotated: isPdfPageRotated ? viewWidth : viewHeight
  };
}

export async function getPageInfo(
  pageNumber: number,
  pdfPageIdentifier: string = 'pageplaceholder'
) {
  const pageNodeInfo = await getPageNodeWithRetry(
    pageNumber,
    pdfPageIdentifier
  );

  if (!pageNodeInfo) {
    throw new Error('PDF page details could not be fetched');
  }

  const { pageNodes, currentPageNode, webPageDetails } = pageNodeInfo;
  const pageLimits = getPageLimits(
    pageNumber,
    pageNodes as HTMLCollectionOf<HTMLElement>
  );

  const pdfPageDetails = await getPage(pageNumber);

  if (
    !pdfPageDetails ||
    !pageLimits ||
    !currentPageNode ||
    !webPageDetails ||
    !pageNodes
  ) {
    throw new Error('PDF page details could not be fetched');
  }

  const { view, rotate } = pdfPageDetails as PDFPageProxy;

  // pdf.js only ever hands out the merged `view` rectangle
  // (CropBox ∩ MediaBox, falling back to MediaBox). The raw boxes are needed
  // to tell an actual crop apart from a MediaBox whose origin is not at 0/0,
  // so they are read back out of the original PDF bytes via pdf-lib. Both
  // fall back to `view` when the document cannot be parsed.
  const pageBoxes = await getPdfPageBoxes(getPdfDocument(), pageNumber);
  const cropBox: number[] = pageBoxes.cropBox ?? view;
  const mediaBox: number[] = pageBoxes.mediaBox ?? view;

  const pageRotation = normalizePageRotation(rotate);
  const viewport = pdfPageDetails!.getViewport({
    scale: Math.min(
      webPageDetails.width / pdfPageDetails!.getViewport({ scale: 1 }).width,
      webPageDetails.height / pdfPageDetails!.getViewport({ scale: 1 }).height
    )
  });

  const { pageXRotated, pageYRotated, pageWidthRotated, pageHeightRotated } =
    getRotatedPageGeometry(view, mediaBox, pageRotation);

  // pdf.js renders the `view` box, so a CropBox that is strictly smaller than
  // the MediaBox is the only thing that makes the rendered page differ from
  // the backend's positioning reference.
  const isPageCropped = pageXRotated > 0 || pageYRotated > 0;

  const pdfJsPageDetails: PdfJsPageDetails = {
    ...pdfPageDetails,
    viewport,
    isPageCropped,
    cropBox,
    mediaBox,
    pageXRotated,
    pageYRotated,
    pageWidthRotated,
    pageHeightRotated
  };

  return {
    pageLimits,
    pdfJsPageDetails,
    webPageDetails,
    pageNodes,
    currentPageNode,
    cropBox,
    mediaBox
  };
}

export function setDefaultVisiblePlaceholderState(
  placeholder: Placeholder,
  currentPageNumber: number
) {
  const position = {
    ...placeholder.position,
    pageNumber: currentPageNumber
  };
  setValues(placeholder, [
    { position },
    { isVisible: true },
    { isAutoPlaced: true },
    { isPlaced: true }
  ]);
}

// `@deepcloud/draggable`'s internal position watcher chains several
// `await nextTick()` calls before it dereferences its DOM ref
// (`context.draggingElement.getBoundingClientRect()`) whenever a Draggable's
// `x`/`y` props change. `nextTick()` on our side only guarantees a single
// microtask hop, so forcing a placeholder's Draggable to remount (via
// `requestPlaceholderRemount`) right after changing its position can tear
// down the DOM node while that chain is still mid-flight, leaving it null and
// throwing "Cannot read properties of null (reading 'getBoundingClientRect')".
// `requestAnimationFrame` only fires once the whole microtask queue (all
// pending/chained `nextTick`s) has drained, so awaiting it guarantees the
// library's watcher has finished reading the DOM before we destroy it.
// Always await this immediately before every `requestPlaceholderRemount` call.
export function waitForDraggableToSettle(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export function pointsToPixels(points: number): number {
  return points / 0.75;
}

export function pixelsToPoints(pixels: number): number {
  return pixels * 0.75;
}

export function setPlaceHolderDimensions(
  placeholder: Placeholder,
  scale: number
) {
  if (!placeholder || !placeholder.position) return;

  const width = pixelsToPoints(constants.SIGNATURE_SIZE.M.WIDTH) * scale;
  const height = pixelsToPoints(constants.SIGNATURE_SIZE.M.HEIGHT) * scale;

  placeholder.position.width = width;
  placeholder.position.height = height;
}

export async function setPlaceHolderResizeThresholds(
  placeholder: Placeholder,
  scale: number
) {
  placeholder.resize.minWidth =
    pixelsToPoints(constants.SIGNATURE_SIZE.XS.WIDTH) * scale;
  placeholder.resize.minHeight =
    pixelsToPoints(constants.SIGNATURE_SIZE.XS.HEIGHT) * scale;

  const { webPageDetails } = await getPageInfo(
    placeholder.position?.pageNumber || 1
  );
  placeholder.resize.maxWidth = webPageDetails.width - 2;
  placeholder.resize.maxHeight = webPageDetails.height - 2;
}

export function scrollToPlaceholder(
  placeholder: Placeholder,
  isEnforceScroll = false
) {
  if (!placeholder?.position) return;
  const { position } = placeholder;
  const scrollableParent = document.querySelector(
    `.page-content`
  ) as HTMLElement;

  const containerRect = scrollableParent.getBoundingClientRect();
  const containerCenterY = containerRect.height / 2;
  const containerCenterX = containerRect.width / 2;

  const isElementInViewport =
    scrollableParent.scrollTop <= position.y - scrollableParent.offsetTop &&
    scrollableParent.scrollLeft <= position.x;

  if (!scrollableParent || (isElementInViewport && !isEnforceScroll)) return;

  const targetScrollTop = position.y + position.height / 2 - containerCenterY;
  const targetScrollLeft = position.x + position.width / 2 - containerCenterX;

  scrollableParent.scrollTo({
    behavior: 'smooth',
    top: Math.max(0, targetScrollTop),
    left: Math.max(0, targetScrollLeft)
  });
}

export async function getResizeValues(
  defaultResizeDimensions: PlaceholderResize,
  currentPageNumber: number = 1
) {
  const { pdfJsPageDetails, webPageDetails } =
    await getPageInfo(currentPageNumber);
  const scale = pdfJsPageDetails.viewport.scale;

  return {
    ...defaultResizeDimensions,
    minWidth: pixelsToPoints(constants.SIGNATURE_SIZE.XS.WIDTH),
    minHeight: pixelsToPoints(constants.SIGNATURE_SIZE.XS.HEIGHT) * scale,
    maxWidth: webPageDetails.width - 2,
    maxHeight: webPageDetails.height - 2
  };
}

export async function getPositionValues(
  position: AutographPosition,
  currentPageNumber: number = 1
) {
  const { pdfJsPageDetails } = await getPageInfo(currentPageNumber);
  const scale = pdfJsPageDetails.viewport.scale;

  return {
    ...position,
    pageNumber: currentPageNumber,
    width: pixelsToPoints(position.width) * scale,
    height: pixelsToPoints(position.height) * scale
  };
}

export async function getAutoPlacementPositions(
  pageNumber: number,
  placeholders: Placeholder[],
  visiblePlaceholders: Placeholder[],
  placeholderTemplateRefs
): Promise<Placeholder[]> {
  const { currentPageNode, pdfJsPageDetails } = await getPageInfo(pageNumber);
  const { scale } = pdfJsPageDetails.viewport;
  if (!currentPageNode || !placeholders || placeholders.length === 0) {
    return placeholders;
  }

  const pageRect = currentPageNode.getBoundingClientRect();
  const pageWidth = pageRect.width;
  const pageHeight = pageRect.height;

  const pageLeft = getLeftOffset(currentPageNode);
  const pageTop = getTopOffset(currentPageNode);

  const gap = 16 * scale;
  const margin = gap;
  const availableWidth = Math.max(0, pageWidth - margin * 2);
  const maxHeight = Math.max(0, pageHeight - margin * 2);

  type Rect = { x: number; y: number; width: number; height: number };

  const toRect = (p: Placeholder): Rect => {
    return {
      x: p.position!.x,
      y: p.position!.y,
      width: p.position!.width,
      height: p.position!.height
    };
  };
  const isColliding = (a: Rect, b: Rect) =>
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y;

  // Only consider visible placeholders on the same page
  const samePageVisiblePlaceholders = cloneDeep(visiblePlaceholders)
    .map((p) => {
      const match = placeholderTemplateRefs.value.find((placeholderRef) => {
        return (
          placeholderRef.context.draggingElement.id === getPlaceholderDomId(p)
        );
      });

      if (p.position?.pageNumber === pageNumber && match) {
        return {
          ...p,
          position: {
            x: match.context.x || p.position.x,
            y: match.context.y || p.position.y,
            width: match.context.width || p.position.width,
            height: match.context.height || p.position.height,
            pageNumber: p.position.pageNumber
          }
        };
      } else return null;
    })
    .filter((placeholder) => placeholder);

  // Start with already visible/placed placeholders as occupied rects
  const occupiedRects: Rect[] = samePageVisiblePlaceholders
    .filter(
      (
        p
      ): p is Placeholder & {
        position: NonNullable<Placeholder['position']>;
      } => !!p && !!p.position
    )
    .map((p) => toRect(p));

  // Derive rows from occupied rects so we can reuse rows when there is space
  interface RowInfo {
    top: number;
    bottom: number;
  }
  const rows: RowInfo[] = [];

  for (const r of occupiedRects) {
    const rectTop = r.y;
    const rectBottom = r.y + r.height;
    const verticalCenter = rectTop + r.height / 2;

    let placedInRow = false;
    for (const row of rows) {
      // assign to row if vertical center is within gap of existing row
      if (
        verticalCenter >= row.top - gap &&
        verticalCenter <= row.bottom + gap
      ) {
        row.top = Math.min(row.top, rectTop);
        row.bottom = Math.max(row.bottom, rectBottom);
        placedInRow = true;
        break;
      }
    }

    if (!placedInRow) {
      rows.push({
        top: rectTop,
        bottom: rectBottom
      });
    }
  }

  // Helper to try placing a rect in a specific row by scanning horizontally
  const tryPlaceInRow = (
    row: RowInfo,
    rectWidth: number,
    rectHeight: number
  ): { x: number; y: number } | null => {
    const rowY =
      row.bottom - rectHeight > pageTop + margin
        ? row.bottom - rectHeight
        : row.top;
    let candidateX = pageLeft + margin;

    let attempts = 0;
    const maxAttempts = 100;

    while (
      candidateX + rectWidth <= pageLeft + margin + availableWidth &&
      attempts <= maxAttempts
    ) {
      const candidate: Rect = {
        x: candidateX,
        y: rowY,
        width: rectWidth,
        height: rectHeight
      };

      const collides = occupiedRects.some((r) => isColliding(candidate, r));
      if (!collides) {
        return { x: candidateX, y: rowY };
      }

      attempts++;
      candidateX += rectWidth + gap;
    }

    return null;
  };

  // Determine starting cursor based on last occupied row,
  // but we will first try to reuse existing rows when placing
  let cursorX = pageLeft + margin;
  // start roughly from half the page height
  let cursorY = pageTop + margin + maxHeight / 2;
  let currentRowHeight = 0;

  if (rows.length > 0) {
    const lastRow = rows[rows.length - 1]!;
    currentRowHeight = lastRow.bottom - lastRow.top;
    cursorY = lastRow.bottom + gap;
  }

  // Clamp starting cursorY inside page
  if (cursorY + currentRowHeight > pageTop + margin + maxHeight) {
    cursorY =
      pageTop + margin + Math.max(0, maxHeight - Math.max(currentRowHeight, 0));
  }

  for (const placeholder of placeholders) {
    if (!placeholder.position) continue;

    // Ensure placeholders are not oversized before applying auto-placement.
    enforceDefaultDimensionsIfOversized(placeholder, scale);
    const placeholderWidth = placeholder.position.width;
    const placeholderHeight = placeholder.position.height;

    let candidateX: number | null = null;
    let candidateY: number | null = null;

    // Try to place into an existing row (from top to bottom) to reuse space
    for (const row of rows) {
      const placement = tryPlaceInRow(row, placeholderWidth, placeholderHeight);
      if (placement) {
        candidateX = placement.x;
        candidateY = placement.y;
        break;
      }
    }

    // If we could not place in any existing row, use the "new row" cursor logic
    if (candidateX === null || candidateY === null) {
      candidateX = cursorX;
      candidateY = cursorY;

      let attempts = 0;
      const maxAttempts = 100;

      while (
        occupiedRects.some((r) =>
          isColliding(
            {
              x: candidateX!,
              y: candidateY!,
              width: placeholderWidth,
              height: placeholderHeight
            },
            r
          )
        )
      ) {
        attempts++;
        if (attempts > maxAttempts) {
          console.error('Could not find a suitable position for placeholder');
          break;
        }

        // advance horizontally
        candidateX += placeholderWidth + gap;

        // if does not fit in row, wrap to next row
        if (
          candidateX + placeholderWidth >
          pageLeft + margin + availableWidth
        ) {
          candidateX = pageLeft + margin;
          candidateY += currentRowHeight + gap;
          currentRowHeight = 0;
        }

        // if wrapped/advanced causes vertical overflow, clamp to bottom margin and stop searching
        if (candidateY + placeholderHeight > pageTop + margin + maxHeight) {
          candidateY =
            pageTop + margin + Math.max(0, maxHeight - placeholderHeight);
          break;
        }
      }

      // If vertical overflow occurred immediately at cursorY, clamp and place
      if (candidateY + placeholderHeight > pageTop + margin + maxHeight) {
        candidateY =
          pageTop + margin + Math.max(0, maxHeight - placeholderHeight);
      }
    }

    // apply position
    placeholder.position.y = candidateY;
    placeholder.position.x = candidateX;
    placeholder.position.width = placeholderWidth;
    placeholder.position.height = placeholderHeight;
    placeholder.position.pageNumber = pageNumber;

    // mark occupied
    const newRect: Rect = {
      x: candidateX,
      y: candidateY,
      width: placeholderWidth,
      height: placeholderHeight
    };
    occupiedRects.push(newRect);

    // update rows with the newly placed placeholder
    const rectTop = newRect.y;
    const rectBottom = newRect.y + newRect.height;
    const verticalCenter = rectTop + newRect.height / 2;

    let placedInRow = false;
    for (const row of rows) {
      if (
        verticalCenter >= row.top - gap &&
        verticalCenter <= row.bottom + gap
      ) {
        row.top = Math.min(row.top, rectTop);
        row.bottom = Math.max(row.bottom, rectBottom);
        placedInRow = true;
        break;
      }
    }
    if (!placedInRow) {
      rows.push({ top: rectTop, bottom: rectBottom });
    }

    // advance cursor for potential new rows later
    cursorX = candidateX + placeholderWidth + gap;
    currentRowHeight = Math.max(currentRowHeight, placeholderHeight);

    if (cursorX + placeholderWidth > pageLeft + margin + availableWidth) {
      cursorX = pageLeft + margin;
      cursorY += currentRowHeight + gap;
      currentRowHeight = 0;
    }

    if (cursorY + placeholderHeight > pageTop + margin + maxHeight) {
      cursorY = pageTop + margin + Math.max(0, maxHeight - placeholderHeight);
    }
  }

  return placeholders;
}

export function getPdfPageFromPoint(clientX: number, clientY: number) {
  const pdfPreview = document.getElementById('pdf-preview');
  if (!(pdfPreview instanceof HTMLElement)) return undefined;

  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof HTMLElement)) continue;

    const page = element.closest('[data-page-number]');

    if (page instanceof HTMLElement && pdfPreview.contains(page)) {
      return page;
    }
  }

  return undefined;
}

export function getPageNumber(page: HTMLElement) {
  const fromDataset = page.dataset.pageNumber;
  if (fromDataset) return Number(fromDataset);

  const fromId = page.id.match(/\d+/)?.[0];
  return fromId ? Number(fromId) : 1;
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
